/******************************************************
 *  EMPLOYEE LEAVE PORTAL — FINAL FULL VERSION
 *  WITH MULTIPLE FILE UPLOAD + PREVIEW + TABLE ICON
 *  ZOHO / KEKA UI — POPPINS + ROBOTO
 ******************************************************/

import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    CalendarDaysIcon,
    CreditCardIcon,
    PaperAirplaneIcon,
    XMarkIcon,
    BookmarkIcon,
    BriefcaseIcon,
    UserCircleIcon,
    FolderOpenIcon,
    ExclamationCircleIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    PaperClipIcon,
    EyeIcon,
    TrashIcon,
    ArrowDownTrayIcon
} from "@heroicons/react/24/outline";

/* GLOBAL CONSTANTS --------------------------------------------------- */

const WORKING_HOURS_PER_DAY = 8;

const LEAVE_TYPES = [
    { id: "casual", name: "Casual Leave (CL)", entitlement: 15, used: 3, remaining: 12, color: "bg-indigo-500", icon: CalendarDaysIcon },
    { id: "sick", name: "Sick Leave (SL)", entitlement: 8, used: 0, remaining: 8, color: "bg-red-500", icon: BriefcaseIcon },
    { id: "earned", name: "Earned Leave (EL)", entitlement: 20, used: 2, remaining: 18, color: "bg-green-500", icon: BookmarkIcon },
];

const initialLeaveRequests = [
    {
        id: 101,
        type: "Casual Leave (CL)",
        durationDays: 2,
        durationHours: 16,
        status: "Pending",
        dates: "2024-11-28 - 2024-11-29",
        reason: "Family trip",
        attachments: []
    },
    {
        id: 102,
        type: "Earned Leave (EL)",
        durationDays: 5,
        durationHours: 40,
        status: "Approved",
        dates: "2024-12-24 - 2024-12-30",
        reason: "Vacation",
        attachments: []
    }
];

/* UTILITIES ---------------------------------------------------------- */

const calculateBusinessDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    if (s > e) return 0;

    let count = 0;
    let cur = new Date(s);

    while (cur <= e) {
        const day = cur.getDay();
        if (day !== 0 && day !== 6) count++;
        cur.setDate(cur.getDate() + 1);
    }
    return count;
};

const calculateBusinessHours = (days) => days * WORKING_HOURS_PER_DAY;

/* SMALL COMPONENTS --------------------------------------------------- */

const StatusTag = ({ status }) => {
    const cls =
        status === "Approved"
            ? "bg-green-100 text-green-700 border-green-300"
            : status === "Pending"
            ? "bg-yellow-100 text-yellow-700 border-yellow-300"
            : status === "Rejected"
            ? "bg-red-100 text-red-700 border-red-300"
            : "bg-gray-100 text-gray-700 border-gray-300";

    return (
        <span
            className={`px-2 py-0.5 text-xs font-medium rounded-full border ${cls} font-roboto tracking-wider`}
        >
            {status}
        </span>
    );
};

const MinimalBalanceCard = ({ name, entitlement, used, remaining, color, icon: Icon }) => {
    const pct = entitlement > 0 ? (used / entitlement) * 100 : 0;

    return (
        <motion.div
            className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 cursor-pointer flex flex-col justify-between"
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
        >
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 tracking-wide font-roboto uppercase">{name}</h3>
                <Icon className={`h-5 w-5 ${color.replace("bg-", "text-")}`} />
            </div>

            <div className="flex items-end justify-between">
                <div>
                    <span className="text-4xl font-extrabold font-poppins text-gray-900 leading-none">{remaining}</span>
                    <span className="text-sm text-gray-500 ml-1">days remaining</span>
                </div>
                <div className="text-right">
                    <p className="text-xs text-gray-500">Entitlement: {entitlement}</p>
                    <p className="text-xs text-gray-500">Used: {used}</p>
                </div>
            </div>

            <div className="w-full h-1 mt-4 rounded-full bg-gray-200">
                <div className={`h-1 rounded-full ${color}`} style={{ width: `${pct}%` }} />
            </div>
        </motion.div>
    );
};

/* ATTACHMENT PREVIEW MODAL -------------------------------------------- */

const AttachmentPreviewModal = ({ isOpen, onClose, files }) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.9 }}
                        className="bg-white rounded-xl max-w-3xl w-full p-6 overflow-y-auto max-h-[90vh]"
                    >
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-poppins font-semibold text-gray-800">
                                View Attachments
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-gray-600 hover:text-black"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {files.map((file, idx) => {
                                const isImage = file.type.startsWith("image/");
                                const isPDF = file.type === "application/pdf";

                                return (
                                    <div key={idx} className="border p-4 rounded-lg bg-gray-50">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="font-medium text-gray-700">{file.name}</span>
                                            <a
                                                href={file.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-indigo-600 text-sm font-medium flex items-center"
                                            >
                                                <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                                                Open / Download
                                            </a>
                                        </div>

                                        {isImage && (
                                            <img
                                                src={file.url}
                                                alt="preview"
                                                className="max-h-60 rounded-lg border"
                                            />
                                        )}

                                        {isPDF && (
                                            <div className="border rounded-lg p-4 text-center bg-white">
                                                <p className="font-semibold text-gray-700">PDF File</p>
                                                <p className="text-gray-500 text-sm">Open to view</p>
                                            </div>
                                        )}

                                        {!isImage && !isPDF && (
                                            <div className="border rounded-lg p-4 text-center bg-white">
                                                <p className="font-semibold text-gray-700">Document File</p>
                                                <p className="text-gray-500 text-sm">Open to download</p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/* LEAVE REQUEST MODAL ------------------------------------------------ */

const LeaveRequestModal = ({ isOpen, onClose, leaveTypes, addRequest }) => {
    const [formData, setFormData] = useState({
        type: leaveTypes[0].id,
        startDate: "",
        endDate: "",
        reason: "",
        isEmergency: false,
        attachments: []
    });

    const [errorMessage, setErrorMessage] = useState("");
    const [submittedSuccess, setSubmittedSuccess] = useState(false);

    /* ---- SELECTED LEAVE INFO ----- */
    const selectedType = leaveTypes.find((t) => t.id === formData.type) || leaveTypes[0];

    /* ---- DURATION CALCULATION ----- */

    const { totalDays, totalHours, paidDays, unpaidDays, remainingAfter } =
        useMemo(() => {
            const days = calculateBusinessDays(formData.startDate, formData.endDate);
            const hours = calculateBusinessHours(days);
            const available = selectedType.remaining;

            const paid = Math.min(days, available);
            const unpaid = Math.max(0, days - available);

            return {
                totalDays: days,
                totalHours: hours,
                paidDays: paid,
                unpaidDays: unpaid,
                remainingAfter: available - paid
            };
        }, [formData.startDate, formData.endDate, selectedType]);

    /* ---- HANDLE CHANGE ----- */

    const handleChange = (e) => {
        const { name, type, checked, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
        setErrorMessage("");
        setSubmittedSuccess(false);
    };

    /* ---- FILE UPLOAD LOGIC (MULTIPLE) ----- */

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);

        const formatted = files.map((f) => ({
            name: f.name,
            type: f.type,
            url: URL.createObjectURL(f)
        }));

        setFormData((prev) => ({
            ...prev,
            attachments: [...prev.attachments, ...formatted]
        }));
    };

    const removeAttachment = (index) => {
        setFormData((prev) => {
            const updated = [...prev.attachments];
            updated.splice(index, 1);
            return { ...prev, attachments: updated };
        });
    };

    /* ---- SUBMIT ------ */

    const handleSubmit = (e) => {
        e.preventDefault();

        if (totalDays <= 0) {
            setErrorMessage("Please select valid start & end dates.");
            return;
        }
        if (formData.reason.length < 20) {
            setErrorMessage("Reason must be at least 20 characters.");
            return;
        }

        addRequest({
            id: Date.now(),
            type: selectedType.name,
            durationDays: totalDays,
            durationHours: totalHours,
            status: "Pending",
            dates: `${formData.startDate} - ${formData.endDate}`,
            reason: formData.reason,
            paidDays,
            unpaidDays,
            attachments: formData.attachments
        });

        setSubmittedSuccess(true);
        setTimeout(() => {
            onClose();
            setSubmittedSuccess(false);
            setFormData({
                type: leaveTypes[0].id,
                startDate: "",
                endDate: "",
                reason: "",
                isEmergency: false,
                attachments: []
            });
        }, 2000);
    };

    /* ---- MODAL UI ----- */

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        initial={{ y: -30 }}
                        animate={{ y: 0 }}
                        exit={{ y: -30 }}
                        className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
                    >
                        {/* HEADER */}
                        <div className="flex justify-between items-center p-5 bg-indigo-600 text-white">
                            <h2 className="text-lg font-poppins font-semibold flex items-center">
                                <PaperAirplaneIcon className="h-6 w-6 mr-2" />
                                New Leave Request
                            </h2>
                            <button onClick={onClose}>
                                <XMarkIcon className="h-7 w-7 text-white" />
                            </button>
                        </div>

                        {submittedSuccess && (
                            <div className="p-4 bg-green-50 text-green-700 font-medium text-sm flex items-center">
                                <CheckCircleIcon className="h-5 w-5 mr-2" /> Request submitted successfully!
                            </div>
                        )}

                        {/* BODY */}
                        <div className="overflow-y-auto p-6 flex-1">
                            <form className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                                {/* LEFT 2/3 FORM */}
                                <div className="lg:col-span-2 space-y-5">

                                    {/* Leave Type */}
                                    <div>
                                        <label className="font-semibold text-sm text-gray-700">Leave Type</label>
                                        <select
                                            name="type"
                                            value={formData.type}
                                            onChange={handleChange}
                                            className="w-full p-2 mt-1 border rounded-lg"
                                        >
                                            {leaveTypes.map((t) => (
                                                <option key={t.id} value={t.id}>
                                                    {t.name} (Remaining: {t.remaining})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Dates */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="font-semibold text-sm text-gray-700">Start</label>
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={formData.startDate}
                                                onChange={handleChange}
                                                className="w-full p-2 border rounded-lg"
                                            />
                                        </div>
                                        <div>
                                            <label className="font-semibold text-sm text-gray-700">End</label>
                                            <input
                                                type="date"
                                                name="endDate"
                                                value={formData.endDate}
                                                onChange={handleChange}
                                                className="w-full p-2 border rounded-lg"
                                            />
                                        </div>
                                    </div>

                                    {/* Emergency Flag */}
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            name="isEmergency"
                                            checked={formData.isEmergency}
                                            onChange={handleChange}
                                            className="h-4 w-4"
                                        />
                                        <span className="text-sm font-semibold text-gray-700">
                                            Medical / Emergency Request
                                        </span>
                                    </div>

                                    {/* Reason */}
                                    <div>
                                        <label className="font-semibold text-sm text-gray-700">Reason</label>
                                        <textarea
                                            name="reason"
                                            rows="4"
                                            value={formData.reason}
                                            onChange={handleChange}
                                            className="w-full p-2 border rounded-lg mt-1"
                                            placeholder="Enter at least 20 characters"
                                        />
                                    </div>

                                    {/* Attachments */}
                                    <div>
                                        <label className="font-semibold text-sm text-gray-700">Attachments</label>

                                        <input
                                            type="file"
                                            multiple
                                            onChange={handleFileUpload}
                                            className="mt-2"
                                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                        />

                                        <div className="mt-3 space-y-2">
                                            {formData.attachments.map((f, i) => (
                                                <div key={i} className="flex justify-between items-center p-2 bg-gray-100 rounded-lg">
                                                    <div className="flex items-center space-x-2">
                                                        <PaperClipIcon className="h-5 w-5 text-gray-600" />
                                                        <span className="text-sm text-gray-700">{f.name}</span>
                                                    </div>
                                                    <button
                                                        onClick={() => removeAttachment(i)}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Errors */}
                                    {errorMessage && (
                                        <div className="p-3 bg-red-100 text-red-700 rounded-lg">
                                            <ExclamationCircleIcon className="h-5 w-5 inline-block mr-2" />
                                            {errorMessage}
                                        </div>
                                    )}
                                </div>

                                {/* RIGHT SUMMARY */}
                                <div>
                                    <div className="p-5 bg-gray-50 rounded-lg border space-y-4">
                                        <h3 className="font-poppins text-lg font-semibold">Summary</h3>

                                        <div>
                                            <p className="text-sm text-gray-500">Requested Duration</p>
                                            <p className="text-2xl font-semibold text-indigo-600">
                                                {totalDays} days
                                            </p>
                                            <p className="text-xs text-gray-500">({totalHours} hours)</p>
                                        </div>

                                        <div className="bg-white p-3 rounded-lg border">
                                            <div className="flex justify-between">
                                                <span className="text-green-700 text-sm font-medium">
                                                    Paid Days
                                                </span>
                                                <span className="text-green-700 text-lg font-bold">{paidDays}</span>
                                            </div>
                                            <div className="flex justify-between mt-2 border-t pt-2">
                                                <span className="text-red-700 text-sm font-medium">
                                                    Unpaid Days
                                                </span>
                                                <span className="text-red-700 text-lg font-bold">{unpaidDays}</span>
                                            </div>
                                        </div>

                                        <div
                                            className={`p-3 rounded-lg ${
                                                unpaidDays > 0
                                                    ? "bg-red-50 border border-red-300"
                                                    : "bg-green-50 border border-green-300"
                                            }`}
                                        >
                                            <p className="text-sm font-medium">
                                                Balance After Approval:
                                            </p>
                                            <p className="text-xl font-bold">
                                                {remainingAfter}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* FOOTER */}
                        <div className="p-5 bg-gray-50 flex justify-end space-x-3">
                            <button
                                onClick={onClose}
                                className="px-5 py-2 bg-white border rounded-lg text-gray-700 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                            >
                                Submit Request
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/* MAIN PAGE ---------------------------------------------------------- */

const EmployeeLeavePortal = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [leaveRequests, setLeaveRequests] = useState(initialLeaveRequests);

    const [viewFiles, setViewFiles] = useState([]);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const employeeName = "Alex Rodriguez";
    const employeeRole = "Senior Product Engineer";

    const addRequest = useCallback((req) => {
        setLeaveRequests((prev) => [req, ...prev]);
    }, []);

    const totalBalance = useMemo(
        () => LEAVE_TYPES.reduce((s, t) => s + t.remaining, 0),
        []
    );

    return (
        <div className="min-h-screen bg-gray-50 font-roboto">
            {/* FONTS */}
            <link
                rel="stylesheet"
                href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Roboto:wght@400;500;700&display=swap"
            />

            <div className="max-w-7xl mx-auto py-8 px-5">
                {/* HEADER */}
                <div className="flex justify-between items-center border-b pb-4 mb-8">
                    <h1 className="text-3xl font-poppins font-extrabold text-gray-900">
                        Employee Leave Portal
                    </h1>

                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
                    >
                        New Leave Request
                    </button>
                </div>

                {/* GRID */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                    {/* LEFT SIDEBAR */}
                    <div className="lg:col-span-3">
                        <div className="bg-white p-6 rounded-xl shadow-md border">
                            <div className="text-center">
                                <UserCircleIcon className="h-16 w-16 text-indigo-600 mx-auto" />
                                <h2 className="font-poppins text-lg font-bold mt-3">{employeeName}</h2>
                                <p className="text-sm text-gray-500">{employeeRole}</p>
                            </div>

                            <div className="mt-5 border-t pt-4">
                                <p className="font-semibold text-sm text-gray-700">Paid Leave Balance</p>
                                <div className="mt-3 bg-indigo-50 p-3 rounded-lg flex justify-between">
                                    <span className="text-sm">Available</span>
                                    <span className="text-2xl font-extrabold font-poppins text-indigo-600">
                                        {totalBalance}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT CONTENT */}
                    <div className="lg:col-span-9 space-y-10">

                        {/* LEAVE BALANCES */}
                        <section>
                            <h2 className="text-xl font-poppins font-bold flex items-center mb-5">
                                <CreditCardIcon className="h-6 w-6 text-indigo-600 mr-2" />
                                Current Leave Balances
                            </h2>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {LEAVE_TYPES.map((t, i) => (
                                    <MinimalBalanceCard key={i} {...t} />
                                ))}
                            </div>
                        </section>

                        {/* REQUEST HISTORY */}
                        <section>
                            <h2 className="text-xl font-poppins font-bold flex items-center mb-5">
                                <FolderOpenIcon className="h-6 w-6 text-indigo-600 mr-2" />
                                Request History
                            </h2>

                            <div className="bg-white rounded-xl shadow-md border overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Type</th>
                                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Dates</th>
                                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Duration</th>
                                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Reason</th>
                                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Status</th>

                                                {/* NEW ATTACHMENT COLUMN */}
                                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Attachments</th>

                                                <th className="px-6 py-3"></th>
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-100">
                                            {leaveRequests.map((req) => (
                                                <tr key={req.id} className="hover:bg-gray-50">

                                                    <td className="px-6 py-4 text-sm">{req.type}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-600">{req.dates}</td>
                                                    <td className="px-6 py-4 text-sm font-semibold">
                                                        {req.durationDays} days
                                                        <span className="text-xs text-gray-400 ml-1">
                                                            ({req.durationHours} hrs)
                                                        </span>
                                                    </td>

                                                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                                        {req.reason}
                                                    </td>

                                                    <td className="px-6 py-4">
                                                        <StatusTag status={req.status} />
                                                    </td>

                                                    {/* ATTACHMENT ICON */}
                                                    <td className="px-6 py-4">
                                                        {req.attachments.length > 0 ? (
                                                            <button
                                                                onClick={() => {
                                                                    setViewFiles(req.attachments);
                                                                    setIsPreviewOpen(true);
                                                                }}
                                                                className="flex items-center text-indigo-600 text-sm"
                                                            >
                                                                <PaperClipIcon className="h-5 w-5 mr-1" />
                                                                {req.attachments.length} file(s)
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-400 text-sm">
                                                                No Files
                                                            </span>
                                                        )}
                                                    </td>

                                                    <td className="px-6 py-4 text-right text-sm">
                                                        {req.status === "Pending" ? (
                                                            <button className="text-red-600 text-xs font-medium">
                                                                Cancel
                                                            </button>
                                                        ) : (
                                                            <button className="text-indigo-600 text-xs font-medium">
                                                                View
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* MODALS */}
            <LeaveRequestModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                leaveTypes={LEAVE_TYPES}
                addRequest={addRequest}
            />

            <AttachmentPreviewModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                files={viewFiles}
            />
        </div>
    );
};

export default EmployeeLeavePortal;
